import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {getAccountProfile, getAccountOrders, getReturnOrderProducts, submitReturn, type FieldRule} from "@zaaaz/shared";
import {useFormValidation} from "@/lib/useFormValidation";
import {useI18n} from "./I18nProvider";
import {FormField} from "./FormField";
import {InfoPageHero} from "./InfoPageHero";

type ReturnField = "comanda" | "motiv" | "optiune" | "file" | "banca" | "iban";

function returnRules(optiune: string): FieldRule<ReturnField>[] {
  const rules: FieldRule<ReturnField>[] = [
    {field: "comanda", type: "select", required: true},
    {field: "motiv", required: true},
    {field: "optiune", type: "select", required: true},
    {field: "file", type: "file", required: true}
  ];
  if(optiune === "3") {
    rules.push({field: "banca", required: true}, {field: "iban", required: true});
  }
  return rules;
}

export function ReturnFormView() {
  const {t} = useI18n();
  const [params] = useSearchParams();
  const preOrd = params.get("ord");
  const [orders, setOrders] = useState<{id: number; label: string}[]>([]);
  const [orderId, setOrderId] = useState(preOrd ? parseInt(preOrd, 10) : 0);
  const [products, setProducts] = useState<{productId: number; nume: string; quantity: number; price: number}[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [motiv, setMotiv] = useState("");
  const [optiune, setOptiune] = useState("");
  const [adresa, setAdresa] = useState("");
  const [banca, setBanca] = useState("");
  const [iban, setIban] = useState("");
  const [terms, setTerms] = useState(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const {errors, clearError, validate} = useFormValidation<ReturnField>();
  useEffect(() => {
    getAccountOrders().then((r) => {
      setOrders(r.items.map((o) => ({id: o.id, label: `Order #${o.id} / ${o.date}`})));
    });
    getAccountProfile().catch(() => {});
  }, []);
  useEffect(() => {
    if(orderId > 0) {
      getReturnOrderProducts(orderId).then((r) => {
        setProducts(r.items);
        setSelected([]);
      }).catch(() => setProducts([]));
    } else setProducts([]);
  }, [orderId]);
  function toggleProduct(nume: string) {
    setSelected((s) => s.includes(nume) ? s.filter((x) => x !== nume) : [...s, nume]);
  }
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!terms) { setError(t("Please accept the terms")); return; }
    if(!selected.length) { setError(t("Select at least one product")); return; }
    if(!validate(returnRules(optiune), {comanda: orderId, motiv, optiune, file: files, banca, iban}, t)) return;
    const form = new FormData();
    form.append("orderId", String(orderId));
    form.append("produse", selected.join(", "));
    form.append("motiv", motiv);
    form.append("optiune", optiune);
    form.append("adresa", adresa);
    form.append("banca", banca);
    form.append("iban", iban);
    if(files) Array.from(files).forEach((f) => form.append("file[]", f));
    try {
      const r = await submitReturn(form);
      setMsg(r.message);
      setMotiv("");
      setSelected([]);
    } catch(err) {
      setError(err instanceof Error ? err.message : t("Send failed"));
    }
  }
  return (
    <div className="cc-infoPage cc-returnForm">
      <InfoPageHero title={t("Product return form")} />
      <div className="cc-infoPage__container">
        <div className="cc-returnForm__card">
      <form className="form" id="returForm" onSubmit={onSubmit} noValidate>
        <div className="line">
          <label>{t("I want to return the order in full or in parts")}</label>
          <FormField error={errors.comanda}>
            <select name="comanda" className="text round anim req" value={orderId || ""} onChange={(e) => { setOrderId(parseInt(e.target.value, 10) || 0); clearError("comanda"); }} aria-required="true">
              <option value="" />
              {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </FormField>
        </div>
        {orderId > 0 ? (
          <div className="showProd">
            <div className="line">
              <label>{t("I would like to return the following products")}</label>
              <div className="lineRetur">
                {products.map((p) => (
                  <label key={p.productId}>
                    <input type="checkbox" checked={selected.includes(p.nume)} onChange={() => toggleProduct(p.nume)} />
                    <b>{p.nume}</b> / Qty: {p.quantity} / Price: {p.price}
                  </label>
                ))}
              </div>
            </div>
            <div className="line">
              <label>{t("The reason for the return is")}</label>
              <FormField error={errors.motiv}>
                <textarea name="motiv" className="text round anim req" value={motiv} onChange={(e) => { setMotiv(e.target.value); clearError("motiv"); }} aria-required="true" />
              </FormField>
            </div>
            <div className="line">
              <label>{t("My return options are")}</label>
              <FormField error={errors.optiune}>
                <select name="optiune" className="text round anim req" value={optiune} onChange={(e) => { setOptiune(e.target.value); clearError("optiune"); clearError("banca"); clearError("iban"); }} aria-required="true">
                  <option value="" />
                  <option value="1">{t("I want to replace the product with a new one")}</option>
                  <option value="2">{t("I want to replace it with another product")}</option>
                  <option value="3">{t("I want to receive cash value")}</option>
                </select>
              </FormField>
            </div>
            <div className="line">
              <label>{t("Images with the product (s)")}</label>
              <FormField error={errors.file}>
                <input type="file" name="file[]" multiple className="text round anim req" onChange={(e) => { setFiles(e.target.files); clearError("file"); }} aria-required="true" />
              </FormField>
            </div>
            <hr />
            <div className="line">
              <label>{t("Address")}</label>
              <textarea name="adresa" className="text round anim" value={adresa} onChange={(e) => setAdresa(e.target.value)} />
            </div>
            <div className="cols2 clearfix">
              <div className="line">
                <label>{t("Bank")}</label>
                <FormField error={errors.banca}>
                  <input type="text" name="banca" className={`text round anim ${optiune === "3" ? "req" : ""}`} value={banca} onChange={(e) => { setBanca(e.target.value); clearError("banca"); }} aria-required={optiune === "3"} />
                </FormField>
              </div>
              <div className="line">
                <label>IBAN</label>
                <FormField error={errors.iban}>
                  <input type="text" name="iban" className={`text round anim ${optiune === "3" ? "req" : ""}`} value={iban} onChange={(e) => { setIban(e.target.value); clearError("iban"); }} aria-required={optiune === "3"} />
                </FormField>
              </div>
            </div>
            <div className="line wCheck" id="checkboxTerms">
              <label><input type="checkbox" name="terms" checked={terms} onChange={(e) => setTerms(e.target.checked)} /> {t("I agree to this notification form regarding my withdrawal from the contract")}</label>
            </div>
            {error ? <p className="round not">{error}</p> : null}
            {msg ? <p className="round ok">{msg}</p> : null}
            <div className="line"><input type="submit" className="buton round anim" value={t("Send")} /></div>
          </div>
        ) : null}
      </form>
        </div>
      </div>
    </div>
  );
}
